import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBanners, deleteBanner } from "../../redux/actions/bannerActions";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaImage, FaEye, FaCalendarAlt, FaTrash } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import SearchBar from "../Components/SearchBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { FullScreenLoader } from "../Components/Loader";

const BannerList = () => {
  const dispatch = useDispatch();
  const { loading, banners, error, count, deleting } = useSelector((state) => state.banner);
  const { userInfo } = useSelector((state) => state.auth);
  const dashboardData = useSelector((state) => state.dashboard.data);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [filterStatus, setFilterStatus] = useState("all"); // all, live, inactive
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { bannerId, bannerType }
  const navigate = useNavigate();
  
  // Check if user is role 1 (Dealer) - only they can delete banners
  const roleId = userInfo?.role_id || dashboardData?.role_id;
  const canDelete = roleId === "1" || roleId === 1;

  useEffect(() => {
    if (userInfo?.access_token) {
      const filters = {};
      if (userInfo?.user_id) {
        filters.client_id = userInfo.user_id;
      }
      if (filterStatus !== "all") {
        filters.is_live = filterStatus === "live";
      }
      dispatch(getBanners(filters, userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token, userInfo?.user_id, filterStatus]);

  const handleDeleteBanner = async (bannerId, bannerType) => {
    try {
      await dispatch(deleteBanner(bannerId, userInfo.access_token));
      // Refresh the banner list after deletion
      const filters = {};
      if (userInfo?.user_id) {
        filters.client_id = userInfo.user_id;
      }
      if (filterStatus !== "all") {
        filters.is_live = filterStatus === "live";
      }
      dispatch(getBanners(filters, userInfo.access_token));
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting banner:", error);
    }
  };

  if (error && !banners.length) {
    return (
      <DashboardLayout>
        <div className="p-4">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </DashboardLayout>
    );
  }

  const bannerArray = Array.isArray(banners) ? banners : [];

  const filteredBanners = bannerArray
    .filter((banner) => {
      const search = searchValue.toLowerCase();
      const bannerType = banner.banner_type?.toLowerCase() || "";
      const clientName = banner.client_name?.toLowerCase() || "";
      const roleNames = (banner.role_names || []).join(" ").toLowerCase();
      return (
        bannerType.includes(search) ||
        clientName.includes(search) ||
        roleNames.includes(search)
      );
    })
    .filter((banner) => {
      if (!startDate && !endDate) return true;

      if (!banner.created_at) return false;

      const createdDate = dayjs(banner.created_at);
      if (!createdDate.isValid()) return false;

      const start = startDate ? dayjs(startDate).startOf("day") : null;
      const end = endDate ? dayjs(endDate).endOf("day") : null;

      if (start && createdDate.isBefore(start)) return false;
      if (end && createdDate.isAfter(end)) return false;

      return true;
    })
    .filter((banner) => {
      if (filterStatus === "all") return true;
      return banner.is_live === (filterStatus === "live");
    });

  return (
    <DashboardLayout>
      <FullScreenLoader 
        show={loading} 
        message="Loading banners..." 
        variant="primary"
        blurIntensity="md"
      />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h2 className="text-2xl font-bold text-amber-600 mb-1">
              Banner Management
            </h2>
            <button
              onClick={() => navigate("/dashboard/createbanner")}
              className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Banner</span>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="flex-1">
              {/* <SearchBar
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search by banner type, client, or role..."
              /> */}
              <SearchBar
                value={searchValue}
                onChange={setSearchValue}
                placeholder="Search by banner type, client, or role..."
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                isClearable={true}
                placeholderText="Select date range"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 text-sm"
              />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="live">Live</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {filteredBanners.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <FaImage className="mx-auto text-gray-400 text-5xl mb-4" />
            <p className="text-gray-600 text-lg">No banners found</p>
            <p className="text-gray-500 text-sm mt-2">
              {searchValue || filterStatus !== "all" || (startDate || endDate)
                ? "Try adjusting your filters"
                : "Create your first banner to get started"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBanners.map((banner) => (
              <div
                key={banner.id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                <div className="relative h-48 bg-gray-200">
                  {banner.url_link ? (
                    <img
                      src={banner.url_link}
                      alt={banner.banner_type}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x200?text=Image+Not+Available";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <FaImage className="text-gray-400 text-4xl" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        banner.is_live
                          ? "bg-green-500 text-white"
                          : "bg-gray-500 text-white"
                      }`}
                    >
                      {banner.is_live ? "Live" : "Inactive"}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {banner.banner_type}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Client:</span>
                      <span>{banner.client_name || "N/A"}</span>
                    </div>
                    {banner.role_names && banner.role_names.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Roles:</span>
                        <span>{banner.role_names.join(", ")}</span>
                      </div>
                    )}
                    {banner.start_date && (
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-gray-400" />
                        <span className="font-medium">Start:</span>
                        <span>{dayjs(banner.start_date).format("DD MMM YYYY")}</span>
                      </div>
                    )}
                    {banner.end_date && (
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-gray-400" />
                        <span className="font-medium">End:</span>
                        <span>{dayjs(banner.end_date).format("DD MMM YYYY")}</span>
                      </div>
                    )}
                    {banner.created_at && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Created: {dayjs(banner.created_at).format("DD MMM YYYY")}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between gap-2">
                    <button
                      onClick={() => window.open(banner.url_link, "_blank")}
                      className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm"
                    >
                      <FaEye className="w-4 h-4" />
                      <span>View Image</span>
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => setDeleteConfirm({ bannerId: banner.id, bannerType: banner.banner_type })}
                        disabled={deleting}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaTrash className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredBanners.length > 0 && (
          <div className="mt-6 text-center text-gray-600 text-sm">
            Showing {filteredBanners.length} of {count} banner(s)
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">
                Confirm Deactivation
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to deactivate the banner "{deleteConfirm.bannerType}"? 
                This will hide it from the list but won't delete it permanently.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteBanner(deleteConfirm.bannerId, deleteConfirm.bannerType)}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? "Deactivating..." : "Deactivate"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BannerList;

